"use client";
import { Card, CardContent, CardHeader, CardTitle } from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "../../../components/ui/dropdown-menu";
import { MoreVertical, FileDown, Edit, Info, CheckCircle, RotateCw, Clock, XCircle } from "lucide-react";
import { Order } from "../types/order";
import axios from "axios";
import { useState, memo } from "react";
import { useSelector } from "react-redux";
import { API_BASE_URL } from "../../../config/api";
import { useOverlayHistory } from "../../../hooks/useOverlayHistory";
import {
  groupProductsForProductionCopy,
  buildProductionSizeLineHtml,
  formatProductionSerialLabel,
  formatProductDisplayName,
  formatPlaceLabel,
  type ProductionCopyGroup,
} from "./orderCalculations";
import { OrderProductsDetailsSection } from "./OrderProductsDetailsSection";
import {
  formatIndiaDateTime,
  formatIndiaDate,
  formatQuotationDateTime,
  shouldShowCustomerContactOnProductionCopy,
  getToBeDeliveredFieldLabel,
} from "../../../utils/dateFormat";

const formatIndiaTime = formatIndiaDateTime;
const formatDateOnly = formatIndiaDate;

const getLocalDateValue = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

interface Props {
  order: Order;
  refresh: () => void;
  onEdit: (order: Order) => void;
}

export const OrderCard: React.FC<Props> = memo(({ order, refresh ,onEdit}) => {
  const [showOrderDetails,setShowOrderDetails]=useState<Order | null>(null)

  useOverlayHistory(!!showOrderDetails, () => setShowOrderDetails(null), `order-details-${order.id}`);
  const [showInvoiceDateModal, setShowInvoiceDateModal] = useState(false);
  const [invoiceDate, setInvoiceDate] = useState(getLocalDateValue);
  const [invoiceError, setInvoiceError] = useState("");
  const [invoiceLoading, setInvoiceLoading] = useState(false);
  const { authToken = "", id = "" ,role="",username=""} = useSelector(
    (state: any) => state.loginData || {}
  );

  const invoiceGenerated =
    order.invoiceGenerated === true ||
    (order as Order & { invoice_generated_?: boolean }).invoice_generated_ === true;

  const orderStatus = String(order.status || "").trim().toLowerCase();
  const dealerCanEdit =
    role === "dealer" &&
    !invoiceGenerated &&
    (orderStatus === "saved" || orderStatus === "new");
  const staffCanEdit = role !== "dealer" && !invoiceGenerated;
  const canEditOrder = dealerCanEdit || staffCanEdit;

  const loadFullOrder = async (): Promise<Order> => {
    try {
      const res = await axios.get(`${API_BASE_URL}account/${id}/order/${order.id}`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      return (res.data || order) as Order;
    } catch {
      return order;
    }
  };

  const handleStatusChange = async (status: string) => {
    try {
      const full = await loadFullOrder();
      const payload = {
        ...full,
        status,
        createdBy: username,
        // Never flip invoice flag via status menus
        invoiceGenerated: Boolean(full.invoiceGenerated),
      };
      await axios.put(`${API_BASE_URL}account/${id}/order/update`, payload, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      refresh();
    } catch (e) {
      console.error("Error updating order status:", e);
      alert(
        axios.isAxiosError(e)
          ? e.response?.data?.message || "Unable to update order status."
          : "Unable to update order status."
      );
    }
  };

const handleDownloadPDF = async () => {
  try {
    const full = await loadFullOrder();
    const [{ QuotationTemplate }, { downloadPDF }] = await Promise.all([
      import("./QuotationTemplate"),
      import("../../../utils/pdfGenerator"),
    ]);
    await downloadPDF(
      <QuotationTemplate order={full} />,
      `Quotation_${full.customerName || "Order"}.pdf`
    );
  } catch (error) {
    console.error("Error downloading PDF:", error);
    alert("Failed to download PDF");
  }
};

const handleDownloadPrintFormatPDF = async () => {
  try {
    const full = await loadFullOrder();
    const [{ QuotationTemplate }, { downloadPDF }] = await Promise.all([
      import("./QuotationTemplate_ForPrint"),
      import("../../../utils/pdfGenerator"),
    ]);
    await downloadPDF(
      <QuotationTemplate order={full} />,
      `Quotation_PrintFormat_${full.customerName || "Order"}.pdf`
    );
  } catch (error) {
    console.error("Error downloading print format PDF:", error);
    alert("Failed to download print format PDF");
  }
};

const handleSubmit = async () => {
    if (!invoiceDate) {
      setInvoiceError("Please select an Invoice Date.");
      return;
    }

    try {
        setInvoiceLoading(true);
        setInvoiceError("");
        const full = await loadFullOrder();
        const payload = {
          ...full,
          createdBy: username,
          invoiceGenerated: true,
          // Store at noon in the business timezone so the calendar date is stable.
          invoiceDate: `${invoiceDate}T12:00:00+05:30`,
        };
        await axios.put(`${API_BASE_URL}account/${id}/order/update`, payload, {
          headers: { Authorization: `Bearer ${authToken}` },
        });
        setShowInvoiceDateModal(false);
        refresh();
    } catch (e) {
      console.error("Error saving order:", e);
      setInvoiceError(
        axios.isAxiosError(e)
          ? e.response?.data?.message || "Unable to generate the invoice."
          : "Unable to generate the invoice."
      );
    } finally {
      setInvoiceLoading(false);
    }
  };

const renderProductionSizesHtml = (group: ProductionCopyGroup) =>
  (group.sizes || [])
    .map(
      (size) => `
      <div class="product-field">
        <span class="product-label">${formatProductionSerialLabel(size.serialNumbers)} =</span>
        <span class="product-val">${buildProductionSizeLineHtml(size)}</span>
      </div>`
    )
    .join("");

const totalQtyForGroup = (group: ProductionCopyGroup) =>
  (group.sizes || []).reduce((sum, size) => sum + (Number(size.quantity) || 0), 0);

const renderProductionTotalQtyHtml = (group: ProductionCopyGroup) => {
  if ((group.sizes || []).length < 2) return "";
  const total = totalQtyForGroup(group);
  return `
    <div class="product-field total-qty">
      <span class="product-label">Total qty :</span>
      <span class="product-val">${total}</span>
    </div>`;
};

const renderProductionSpecsHtml = (
  specEntries: Array<{ key: string; value: string }>
) => {
  if (!specEntries || specEntries.length === 0) return "";
  const rows: Array<Array<{ key: string; value: string }>> = [];
  for (let i = 0; i < specEntries.length; i += 3) {
    rows.push(specEntries.slice(i, i + 3));
  }
  return rows
    .map((row) => {
      const width = `${(100 / row.length).toFixed(4)}%`;
      const cells = row
        .map(
          ({ key, value }) => `
        <div class="spec-cell" style="width:${width}">
          <strong>${key.replace(/_/g, " ")}:</strong> ${value || "N/A"}
        </div>`
        )
        .join("");
      return `<div class="spec-row">${cells}</div>`;
    })
    .join("");
};

const handlePrintProductionCopy = async () => {
  const printWindow = window.open("", "_blank");
  if (!printWindow) {
    alert("Please allow popups for this website");
    return;
  }

  const printOrder = await loadFullOrder();
  const companyName = String(
    (printOrder as Order & { company_name_?: string; companyName?: string }).company_name_ ||
      (printOrder as Order & { companyName?: string }).companyName ||
      ""
  ).trim();
  const customerName = String(printOrder.customerName || "").trim() || "N/A";
  const customerDisplay = companyName ? `${customerName} (${companyName})` : customerName;
  const escapeHtml = (value: string) =>
    String(value || "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;");

  const grouped = groupProductsForProductionCopy(printOrder.products);
  let productsHTML = "";
  if (grouped.length > 0) {
    productsHTML = grouped
      .map((group) => {
        const specEntriesForPrint = (group.specEntries || []).map((e) =>
          String(e.key || "").trim().toLowerCase() === "place"
            ? { ...e, value: formatPlaceLabel(String(e.value ?? "")) }
            : e
        );
        const hasPlaceSpec = specEntriesForPrint.some(
          (e) => String(e.key || "").trim().toLowerCase() === "place"
        );
        if (!hasPlaceSpec && group.place) {
          specEntriesForPrint.push({ key: "Place", value: formatPlaceLabel(group.place) });
        }
        const specHTML = renderProductionSpecsHtml(specEntriesForPrint);
        const placeLabel = formatPlaceLabel(group.place);

        return `
  <div class="product">
    <div class="product-name">${escapeHtml(formatProductDisplayName(group.name || "N/A", placeLabel))}</div>
    ${specHTML}
    ${renderProductionSizesHtml(group)}
    ${renderProductionTotalQtyHtml(group)}
  </div>`;
      })
      .join("");
  } else {
    productsHTML = "<p style='font-size: 10px;'>No products available</p>";
  }

 


  const productionCopyHTML = `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="UTF-8">
      <title>Production Copy</title>
      <style>
        @page {
          size: 4in 6in;
          margin: 0;
        }
        @media print {
          body {
            margin: 0;
            padding: 0;
          }
          .product {
            page-break-inside: avoid;
            break-inside: avoid;
          }
        }
        body {
          font-family: Arial, sans-serif;
          width: 4in;
          min-height: 6in;
          height: auto;
          margin: 0;
          padding: 8px;
          box-sizing: border-box;
          background: white;
        }
        .header {
          text-align: center;
          font-weight: bold;
          font-size: 18px;
          margin-bottom: 8px;
          border-bottom: 2px solid black;
          padding-bottom: 4px;
        }
        .info-row {
          display: grid;
          grid-template-columns: minmax(11rem, 42%) 1fr;
          column-gap: 8px;
          align-items: baseline;
          font-size: 13px;
          margin-bottom: 6px;
          line-height: 1.3;
        }
        .info-row.note-row {
          font-size: 15px;
          align-items: flex-start;
        }
        .info-label {
          font-weight: bold;
          white-space: nowrap;
        }
        .info-row.note-row .info-label,
        .info-row.note-row .info-value {
          font-size: 15px;
        }
        .info-value {
          word-break: break-word;
          min-width: 0;
        }
        .section-divider {
          border-top: 1px solid black;
          margin: 6px 0;
        }
        .product {
          margin-bottom: 8px;
          border: 1px solid black;
          padding: 4px;
          page-break-inside: avoid;
          break-inside: avoid;
          -webkit-column-break-inside: avoid;
        }
        .product-name {
          font-weight: bold;
          font-size: 14px;
          margin-bottom: 4px;
          text-align: center;
          background: #f0f0f0;
          padding: 2px;
        }
      .product-field {
  display: flex;
  font-size: 12px;
  line-height: 1.3;
  border-top: 1px solid #000;
}

      .product-label {
  font-weight: bold;
  width: 35%;
  padding: 2px 4px;
  border-right: 1px solid #000;
  box-sizing: border-box;
}
        .product-val {
  width: 65%;
  padding: 2px 4px;
  word-break: break-word;
  box-sizing: border-box;
}
        .dim-row {
          display: flex;
          align-items: stretch;
          border-top: 1px solid #000;
          font-size: 12px;
        }
        .dim-cell {
          flex: 1;
          padding: 2px 4px;
          border-right: 1px solid #000;
          box-sizing: border-box;
        }
        .dim-cell:last-child {
          border-right: none;
        }
        .dim-cell strong {
          display: block;
        }
        .note-value {
          white-space: pre-wrap;
          min-height: 22px;
          font-size: 15px;
        }
        .total-qty {
          font-weight: bold;
        }
        .spec-row {
          display: flex;
          border-top: 1px solid #000;
          font-size: 12px;
          line-height: 1.3;
        }
        .spec-cell {
          box-sizing: border-box;
          padding: 2px 4px;
          border-right: 1px solid #000;
          word-break: break-word;
        }
        .spec-cell:last-child {
          border-right: none;
        }
        .spec-cell strong {
          display: inline;
        }
        .overall-total-qty {
          margin-top: 8px;
          border: 2px solid black;
          padding: 4px 8px;
          font-weight: bold;
          font-size: 14px;
          display: flex;
          justify-content: space-between;
          background: #f0f0f0;
          page-break-inside: avoid;
          break-inside: avoid;
        }
      </style>
    </head>
    <body>
      <div class="header">Production Copy</div>
      
      <div class="info-row">
        <span class="info-label">Customer Name :</span>
        <span class="info-value">${escapeHtml(customerDisplay)}</span>
      </div>

      <div class="info-row">
        <span class="info-label">Site Address :</span>
        <span class="info-value">${escapeHtml(String((printOrder as Order & { siteAddress?: string }).siteAddress || "N/A"))}</span>
      </div>

      <div class="info-row">
        <span class="info-label">Order Number :</span>
        <span class="info-value">${escapeHtml(String(printOrder.id || "N/A"))}</span>
      </div>
      
      ${
        shouldShowCustomerContactOnProductionCopy(printOrder.customerRole)
          ? `
      <div class="info-row">
        <span class="info-label">Mobile Number :</span>
        <span class="info-value">${escapeHtml(String(printOrder.mobile || "N/A"))}</span>
      </div>
      
      <div class="info-row">
        <span class="info-label">Address :</span>
        <span class="info-value">${escapeHtml(String(printOrder.address_ || "N/A"))}</span>
      </div>`
          : ""
      }

      <div class="info-row">
        <span class="info-label">Created On :</span>
        <span class="info-value">${formatIndiaTime(printOrder.createdAt)}</span>
      </div>

      <div class="info-row">
        <span class="info-label">Delivery Date :</span>
        <span class="info-value">${formatDateOnly(printOrder.deliveryDate)}</span>
      </div>

      <div class="info-row">
        <span class="info-label">${getToBeDeliveredFieldLabel(printOrder.customerRole)} :</span>
        <span class="info-value">${formatDateOnly(printOrder.toBeDelivered)}</span>
      </div>

      <div class="info-row note-row">
        <span class="info-label">Note :</span>
        <span class="info-value note-value">${escapeHtml(String(printOrder.notes || ""))}\n </span>
      </div>
      
      <div class="section-divider"></div>

      ${productsHTML}

      <div class="overall-total-qty">
        <span>Overall Total Qty :</span>
        <span>${grouped.reduce((sum, g) => sum + (g.sizes || []).reduce((s, sz) => s + (Number(sz.quantity) || 0), 0), 0)}</span>
      </div>
    </body>
    </html>
  `;

  printWindow.document.write(productionCopyHTML);
  printWindow.document.close();

  // Paginate into fixed 4×6" pages and stamp 1, 2, 3… at the bottom center.
  // Reserve footer space so page numbers never cover product tables.
  const stampProductionPageNumbers = () => {
    try {
      const doc = printWindow.document;
      const srcBody = doc.body;
      if (!srcBody) {
        printWindow.focus();
        printWindow.print();
        return;
      }

      const PAGE_W = 384; // 4in at 96dpi
      const PAGE_H = 576; // 6in at 96dpi
      const PAD_X = 8;
      const PAD_TOP = 8;
      const FOOTER_ZONE = 36; // reserved bottom strip for page number
      const SAFETY = 6; // keep a little gap above the footer
      const contentH = PAGE_H - PAD_TOP - FOOTER_ZONE - SAFETY;

      const clone = srcBody.cloneNode(true) as HTMLElement;
      clone.querySelectorAll(".page-number-footer").forEach((n) => n.remove());

      const wrapper = doc.createElement("div");
      wrapper.id = "paged-root";
      wrapper.style.cssText = `width:${PAGE_W}px;margin:0;padding:0;`;

      const measure = doc.createElement("div");
      measure.style.cssText = `position:absolute;left:-99999px;top:0;width:${PAGE_W - PAD_X * 2}px;padding:0 ${PAD_X}px;box-sizing:border-box;font-family:Arial,sans-serif;`;
      while (clone.firstChild) measure.appendChild(clone.firstChild);
      doc.body.appendChild(measure);

      const blockHeight = (el: HTMLElement) => {
        const rect = el.getBoundingClientRect();
        const cs = printWindow.getComputedStyle(el);
        const mt = parseFloat(cs.marginTop) || 0;
        const mb = parseFloat(cs.marginBottom) || 0;
        return (rect.height || el.offsetHeight || 0) + mt + mb;
      };

      const isOverallTotalQty = (el: HTMLElement) =>
        el.classList.contains("overall-total-qty");

      const pageUsedHeight = (blocks: HTMLElement[]) =>
        blocks.reduce((sum, b) => sum + blockHeight(b), 0);

      const kids = Array.from(measure.children) as HTMLElement[];
      const pages: HTMLElement[][] = [];
      let current: HTMLElement[] = [];
      let used = 0;

      const pushPage = () => {
        if (current.length) pages.push(current);
        current = [];
        used = 0;
      };

      kids.forEach((el) => {
        const h = blockHeight(el);

        if (
          isOverallTotalQty(el) &&
          current.length > 0 &&
          used + h > contentH
        ) {
          const last = current[current.length - 1];
          if (last && !isOverallTotalQty(last)) {
            const lastH = blockHeight(last);
            const withoutLast = used - lastH;
            if (withoutLast + h <= contentH) {
              current.pop();
              used = withoutLast;
              pushPage();
              current.push(last);
              used += lastH;
            } else {
              pushPage();
            }
          } else {
            pushPage();
          }
        } else if (current.length && used + h > contentH) {
          pushPage();
        }

        if (h > contentH && current.length === 0) {
          current.push(el);
          pushPage();
          return;
        }
        current.push(el);
        used += h;
      });
      pushPage();

      if (pages.length >= 2) {
        const lastPage = pages[pages.length - 1];
        if (
          lastPage.length === 1 &&
          isOverallTotalQty(lastPage[0])
        ) {
          const prevPage = pages[pages.length - 2];
          const prevUsed = pageUsedHeight(prevPage);
          const footerH = blockHeight(lastPage[0]);
          if (prevUsed + footerH <= contentH) {
            prevPage.push(lastPage[0]);
            pages.pop();
          }
        }
      }

      measure.remove();
      srcBody.innerHTML = "";
      srcBody.style.cssText = `margin:0;padding:0;width:${PAGE_W}px;background:white;`;

      pages.forEach((pageKids, idx) => {
        const page = doc.createElement("div");
        page.className = "print-page";
        page.style.cssText = `position:relative;width:${PAGE_W}px;height:${PAGE_H}px;box-sizing:border-box;padding:${PAD_TOP}px ${PAD_X}px ${FOOTER_ZONE}px ${PAD_X}px;page-break-after:always;break-after:page;overflow:hidden;`;

        const content = doc.createElement("div");
        content.style.cssText = `max-height:${contentH}px;overflow:visible;box-sizing:border-box;`;
        pageKids.forEach((k) => content.appendChild(k));
        page.appendChild(content);

        const footer = doc.createElement("div");
        footer.className = "page-number-footer";
        footer.style.cssText =
          "position:absolute;left:0;right:0;bottom:10px;height:20px;line-height:20px;text-align:center;font-family:Arial,sans-serif;font-size:11px;font-weight:bold;z-index:2;background:white;";
        footer.textContent = String(idx + 1);
        page.appendChild(footer);
        wrapper.appendChild(page);
      });

      const last = wrapper.lastElementChild as HTMLElement | null;
      if (last) {
        last.style.pageBreakAfter = "auto";
        last.style.breakAfter = "auto";
      }

      srcBody.appendChild(wrapper);

      const style = doc.createElement("style");
      style.textContent = `
        @page { size: 4in 6in; margin: 0; }
        @media print {
          html, body { width: 4in; margin: 0; padding: 0; }
          .print-page { page-break-after: always; break-after: page; }
          .print-page:last-child { page-break-after: auto; break-after: auto; }
          .page-number-footer { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        }
      `;
      doc.head.appendChild(style);
    } catch (e) {
      console.warn("Production copy page numbering fallback:", e);
    }
    setTimeout(() => {
      printWindow.focus();
      printWindow.print();
    }, 250);
  };

  if (printWindow.document.readyState === "complete") {
    stampProductionPageNumbers();
  } else {
    printWindow.onload = stampProductionPageNumbers;
  }
};

  return (
    <Card className="p-0 border-2 border-gray-200 rounded-2xl overflow-hidden shadow-sm hover:shadow-xl hover:border-blue-300 transition-all group">
      <CardHeader className="bg-gradient-to-r from-blue-50 to-blue-100 flex justify-between items-start px-2.5 py-2 border-b-2 border-gray-200">
        <div className="flex-1 min-w-0">
          <CardTitle className="text-base font-bold text-gray-900 group-hover:text-blue-700 transition-colors truncate">
            {order.customerName}
          </CardTitle>
          <p className="text-[11px] text-gray-500 mt-0.5 font-medium">
            {orderStatus === "saved" ? (
              <>
                Draft{" "}
                <span className="text-slate-500 font-semibold">(no Order ID until Submit)</span>
              </>
            ) : (
              <>
                Order ID: <span className="text-blue-600 font-semibold">{order.id}</span>
              </>
            )}
            {invoiceGenerated && order.invoiceId ? (
              <>
                {" · "}Invoice:{" "}
                <span className="text-orange-600 font-semibold">{order.invoiceId}</span>
              </>
            ) : null}
          </p>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          {invoiceGenerated && (
            <span className="px-2 py-0.5 rounded-full text-[11px] font-bold bg-orange-100 text-orange-700">
              Invoiced
            </span>
          )}
          <span
            className={`px-2 py-0.5 rounded-full text-[11px] font-bold inline-flex items-center gap-1 ${
              order.status === "completed"
                ? "bg-green-100 text-green-700"
                : order.status === "delivered"
                ? "bg-emerald-100 text-emerald-700"
                : order.status === "inprogress"
                ? "bg-blue-100 text-blue-700"
                : order.status === "cancelled"
                ? "bg-red-100 text-red-700"
                : order.status === "saved"
                ? "bg-slate-100 text-slate-700"
                : order.status === "new"
                ? "bg-sky-100 text-sky-700"
                : "bg-yellow-100 text-yellow-700"
            }`}
          >
            {order.status === "completed" && <CheckCircle className="w-4 h-4" />}
            {order.status === "delivered" && <CheckCircle className="w-4 h-4" />}
            {order.status === "inprogress" && <RotateCw className="w-4 h-4" />}
            {order.status === "pending" && <Clock className="w-4 h-4" />}
            {order.status === "cancelled" && <XCircle className="w-4 h-4" />}
            {order.status === "pending"
              ? "Pending for cancellation"
              : order.status === "saved"
              ? "Saved"
              : order.status.charAt(0).toUpperCase() + order.status.slice(1)}
          </span>
         {role !== "dealer" && <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="hover:bg-white">
                <MoreVertical className="w-4 h-4 text-gray-600" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="bg-white rounded-lg shadow-lg">
              {!invoiceGenerated && (
                <>
                  <DropdownMenuItem
                    onClick={() => handleStatusChange("inprogress")}
                    className="cursor-pointer hover:bg-blue-50 text-gray-700"
                  >
                    Mark as Inprogress
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => handleStatusChange("pending")}
                    className="cursor-pointer hover:bg-blue-50 text-gray-700"
                  >
                    Mark as Pending for Cancellation
                  </DropdownMenuItem>
                  {role === "admin" && (
                    <DropdownMenuItem
                      onClick={() => handleStatusChange("cancelled")}
                      className="cursor-pointer hover:bg-blue-50 text-gray-700"
                    >
                      Mark as Cancelled
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem
                    onClick={() => handleStatusChange("delivered")}
                    className="cursor-pointer hover:bg-emerald-50 text-gray-700"
                  >
                    Mark as Delivered
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => handleStatusChange("completed")}
                    className="cursor-pointer hover:bg-green-50 text-gray-700"
                  >
                    Mark as Completed
                  </DropdownMenuItem>
                </>
              )}
             { !invoiceGenerated && <DropdownMenuItem
                onClick={() => {
                  setInvoiceDate(getLocalDateValue());
                  setInvoiceError("");
                  setShowInvoiceDateModal(true);
                }}
                className="cursor-pointer hover:bg-purple-50 text-gray-700"
              >
                Generate Invoice
              </DropdownMenuItem>}
              {(role === "admin" || role === "employee") && order.status === "inprogress" && (
                <DropdownMenuItem
                  onClick={() => handlePrintProductionCopy()}
                  className="cursor-pointer hover:bg-orange-50 text-gray-700"
                >
                  Production Copy
                </DropdownMenuItem>
              )}
              {order?.quotation && (
                <DropdownMenuItem
                  onClick={() => handleDownloadPrintFormatPDF()}
                  className="cursor-pointer hover:bg-emerald-50 text-gray-700"
                >
                  Download Print Format
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>}
        </div>
      </CardHeader>

      <CardContent className="px-2.5 py-2 space-y-1.5">
        <div className="grid grid-cols-2 gap-1.5">
          <div className="bg-gray-50 border border-gray-200 rounded-md px-2 py-1.5">
            <p className="text-[10px] leading-tight text-gray-500 font-semibold mb-0.5">Created By</p>
            <p className="text-xs font-bold text-gray-900 truncate leading-snug">{order.createdBy || "N/A"}</p>
          </div>
          {(order.quotationDate || order.quotationTime) ? (
            <div className="bg-amber-50 border border-amber-200 rounded-md px-2 py-1.5 min-w-0">
              <p className="text-[10px] leading-tight text-amber-700 font-semibold mb-0.5">Quotation Date & Time</p>
              <p className="text-xs font-bold text-gray-900 leading-snug whitespace-nowrap truncate">
                {formatQuotationDateTime(order.quotationDate, order.quotationTime)}
              </p>
            </div>
          ) : (
            <div className="bg-amber-50 border border-amber-200 rounded-md px-2 py-1.5">
              <p className="text-[10px] leading-tight text-amber-700 font-semibold mb-0.5">Quotation Date & Time</p>
              <p className="text-xs font-bold text-gray-900 leading-snug">—</p>
            </div>
          )}
          {role !== "dealer" && (parseFloat(order.advance || "0") > 0 || parseFloat(order.total || "0") > 0) && (
            <>
              <div className="bg-teal-50 border border-teal-200 rounded-md px-2 py-1.5">
                <p className="text-[10px] leading-tight text-teal-700 font-semibold mb-0.5">Advance</p>
                <p className="text-xs font-bold text-gray-900 leading-snug">
                  ₹{parseFloat(order.advance || "0").toFixed(2)}
                  {parseFloat(order.advance2 || "0") > 0
                    ? ` + ₹${parseFloat(order.advance2 || "0").toFixed(2)}`
                    : ""}
                </p>
              </div>
              <div className="bg-slate-50 border border-slate-200 rounded-md px-2 py-1.5">
                <p className="text-[10px] leading-tight text-slate-600 font-semibold mb-0.5">Balance</p>
                <p className="text-xs font-bold text-gray-900 leading-snug">₹{parseFloat(order.balance || "0").toFixed(2)}</p>
              </div>
            </>
          )}
        </div>

        <div className="flex gap-1.5">
          <Button
            variant="outline"
            size="sm"
            onClick={async () => setShowOrderDetails(await loadFullOrder())}
            className="flex-1 min-w-0 px-1.5 text-xs border-blue-300 text-blue-600 hover:bg-blue-50 font-semibold"
          >
            <Info className="w-3.5 h-3.5 mr-0.5 shrink-0" /> View Details
          </Button>
          {canEditOrder && (
            <Button
              variant="outline"
              size="sm"
              onClick={async () => onEdit(await loadFullOrder())}
              className="flex-1 min-w-0 px-1.5 text-xs border-blue-300 text-blue-600 hover:bg-blue-50 font-semibold"
            >
              <Edit className="w-3.5 h-3.5 mr-0.5 shrink-0" /> Edit
            </Button>
          )}
          {order?.quotation && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleDownloadPDF}
              className="flex-1 min-w-0 px-1.5 text-xs border-green-300 text-green-600 hover:bg-green-50 font-semibold"
            >
              <FileDown className="w-3.5 h-3.5 mr-0.5 shrink-0" /> PDF
            </Button>
          )}
        </div>
      </CardContent>

      {/* Order Details Drawer — always render from full order (showOrderDetails), not lite list item */}
      {showOrderDetails && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-40" onClick={() => setShowOrderDetails(null)} />
      )}
      <div
        className={`fixed md:left-1/2 md:top-1/2 md:transform md:-translate-x-1/2 md:-translate-y-1/2 bottom-0 left-0 right-0 md:bottom-auto bg-white z-50 transform transition-all duration-300 w-full md:w-[500px] md:h-auto max-h-screen md:max-h-[95vh] overflow-y-auto rounded-t-2xl md:rounded-lg ${
          showOrderDetails ? "translate-y-0 md:scale-100" : "translate-y-full md:scale-0 md:opacity-0 pointer-events-none"
        }`}
      >
        {showOrderDetails && (() => {
          const details = showOrderDetails;
          const showMoney = !!details.quotation;
          return (
        <>
        <div className="p-6 border-b border-gray-200 sticky top-0 bg-white flex justify-between items-center rounded-t-2xl md:rounded-t-lg">
          <h2 className="text-xl font-bold text-gray-900">
            Order Details - {details.customerName}
          </h2>
          <button
            onClick={() => setShowOrderDetails(null)}
            className="text-gray-500 hover:text-gray-700 text-2xl font-bold"
          >
            ✕
          </button>
        </div>

        <div className="p-6 pb-20 md:pb-6 space-y-4">

          {/* Order Details in Single Box */}
          <div className="bg-white border-2 border-gray-300 rounded-xl p-6 shadow-sm">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Row 1 */}
              <div>
                <p className="text-xs font-bold text-gray-600 uppercase mb-1">Order Number</p>
                <p className="text-base font-bold text-gray-900 break-words">{details.id}</p>
              </div>
              <div>
                <p className="text-xs font-bold text-gray-600 uppercase mb-1">Order Date & Time</p>
                <p className="text-base font-bold text-gray-900 break-words">{formatIndiaTime(details.createdAt)}</p>
              </div>

              {/* Row 2 */}
              <div>
                <p className="text-xs font-bold text-gray-600 uppercase mb-1">Customer Name</p>
                <p className="text-base font-bold text-gray-900 break-words">{details.customerName}</p>
              </div>
              <div>
                <p className="text-xs font-bold text-gray-600 uppercase mb-1">Customer ID</p>
                <p className="text-base font-bold text-gray-900 break-words">{details.customerId}</p>
              </div>

              {/* Row 3 */}
              <div>
                <p className="text-xs font-bold text-gray-600 uppercase mb-1">Created On</p>
                <p className="text-base font-bold text-gray-900 break-words">{formatIndiaTime(details.createdAt)}</p>
              </div>
              <div>
                <p className="text-xs font-bold text-gray-600 uppercase mb-1">Updated Date</p>
                <p className="text-base font-bold text-gray-900 break-words">{formatIndiaTime(details.updatedAt)}</p>
              </div>

              {/* Row 4 */}
              <div>
                <p className="text-xs font-bold text-gray-600 uppercase mb-1">Delivery Date</p>
                <p className="text-base font-bold text-gray-900 break-words">{formatDateOnly(details.deliveryDate)}</p>
              </div>
              <div>
                <p className="text-xs font-bold text-gray-600 uppercase mb-1">{getToBeDeliveredFieldLabel(details.customerRole)}</p>
                <p className="text-base font-bold text-gray-900 break-words">{formatDateOnly(details.toBeDelivered)}</p>
              </div>

              {/* Quotation + advance */}
              <div>
                <p className="text-xs font-bold text-gray-600 uppercase mb-1">Quotation Date & Time</p>
                <p className="text-base font-bold text-gray-900 break-words">
                  {formatQuotationDateTime(details.quotationDate, details.quotationTime)}
                </p>
              </div>
              {showMoney && (
              <div>
                <p className="text-xs font-bold text-gray-600 uppercase mb-1">Advance / Balance</p>
                <p className="text-base font-bold text-gray-900 break-words">
                  ₹{parseFloat(details.advance || "0").toFixed(2)}
                  {parseFloat(details.advance2 || "0") > 0
                    ? ` + ₹${parseFloat(details.advance2 || "0").toFixed(2)}`
                    : ""}{" "}
                  / ₹{parseFloat(details.balance || "0").toFixed(2)}
                </p>
              </div>
              )}
            </div>
          </div>

          {/* Price Summary Table — only when Create Quotation is enabled */}
       {showMoney && (
  <div className="bg-white border-2 border-blue-300 rounded-xl overflow-x-auto">
    <div className="w-full">
      {(() => {
        const hasFixing = parseFloat(details.fixing || "0") > 0;
        const hasTransport = parseFloat(details.transport || "0") > 0;
        const hasDiscount = parseFloat(details.discount || "0") > 0;

        const colCount =
          3 +
          (hasFixing ? 1 : 0) +
          (hasTransport ? 1 : 0) +
          (hasDiscount ? 1 : 0);

        const gridStyle = {
          gridTemplateColumns: `repeat(${colCount}, minmax(0, 1fr))`,
        };

        return (
          <>
            {/* Header Row */}
           <div
  className="grid w-full box-border gap-0 bg-blue-600 text-white font-bold text-sm"
  style={gridStyle}
>
              <div className="p-3 text-center border-r border-blue-500">
                Total
              </div>
              <div className="p-3 text-center border-r border-blue-500">
                GST
              </div>
              {hasFixing && (
                <div className="p-3 text-center border-r border-blue-500">
                  Fixing
                </div>
              )}
              {hasTransport && (
                <div className="p-3 text-center border-r border-blue-500">
                  Transport
                </div>
              )}
              {hasDiscount && (
                <div className="p-3 text-center border-r border-blue-500">
                  Discount
                </div>
              )}
              <div className="p-3 text-center">Total Amount</div>
            </div>

            {/* Data Row */}
           <div
  className="grid w-full box-border gap-0 bg-blue-50"
  style={gridStyle}
>
              <div className="p-3 text-center border-r border-blue-200 overflow-hidden">
                <p className="text-xs font-bold text-gray-900 break-words">
                  ₹{details.price
                    ? parseFloat(details.price.toString()).toFixed(2)
                    : "0.00"}
                </p>
              </div>

              <div className="p-3 text-center border-r border-blue-200 overflow-hidden">
                <p className="text-xs font-bold text-gray-900 break-words">
                  ₹{details.gst
                    ? parseFloat(details.gst.toString()).toFixed(2)
                    : "0.00"}
                </p>
              </div>

              {hasFixing && (
                <div className="p-3 text-center border-r border-blue-200 overflow-hidden">
                  <p className="text-xs font-bold text-gray-900 break-words">
                    ₹{parseFloat(details.fixing ? details.fixing.toString() : "0").toFixed(2)}
                  </p>
                </div>
              )}

              {hasTransport && (
                <div className="p-3 text-center border-r border-blue-200 overflow-hidden">
                  <p className="text-xs font-bold text-gray-900 break-words">
                    ₹{parseFloat(details.transport ? details.transport.toString() : "0").toFixed(2)}
                  </p>
                </div>
              )}

              {hasDiscount && (
                <div className="p-3 text-center border-r border-blue-200 overflow-hidden">
                  <p className="text-xs font-bold text-gray-900 break-words">
                    ₹{parseFloat(details.discount ? details.discount.toString() : "0").toFixed(2)}
                  </p>
                </div>
              )}
              <div className="p-3 text-center bg-green-100 overflow-hidden">
                <p className="text-xs font-bold text-green-700 break-words">
                  ₹{details.total
                    ? parseFloat(details.total.toString()).toFixed(2)
                    : "0.00"}
                </p>
              </div>
            </div>
          </>
        );
      })()}
    </div>
  </div>
)}


          {/* Notes */}
          {details.siteAddress ? (
            <div>
              <p className="text-xs text-gray-500 font-semibold mb-2">Site Address:</p>
              <div className="bg-gray-50 p-3 rounded-lg border border-gray-200 break-all whitespace-pre-wrap overflow-hidden">
                <p className="text-sm font-bold text-gray-900">{details.siteAddress}</p>
              </div>
            </div>
          ) : null}

          {details.notes && (
            <div>
              <p className="text-xs text-gray-500 font-semibold mb-2">Notes:</p>
              <div className="bg-gray-50 p-3 rounded-lg border border-gray-200 break-all whitespace-pre-wrap overflow-hidden">
                <p className="text-sm font-bold text-gray-900">{details.notes}</p>
              </div>
            </div>
          )}

          <OrderProductsDetailsSection
            products={details.products}
            showMoney={showMoney}
            accountId={id}
            authToken={authToken}
            historyKeyPrefix={`order-${order.id}`}
          />
        </div>
        </>
          );
        })()}
      </div>

      {showInvoiceDateModal && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4"
          onClick={() => !invoiceLoading && setShowInvoiceDateModal(false)}
        >
          <div
            className="w-full max-w-sm rounded-xl bg-white p-5 shadow-2xl"
            onClick={(event) => event.stopPropagation()}
          >
            <h2 className="text-lg font-bold text-gray-900">Generate Invoice</h2>
            <p className="mt-1 text-sm text-gray-500">
              Select the business date to print on this invoice.
            </p>
            <label className="mt-4 block text-sm font-semibold text-gray-700">
              Invoice Date
              <input
                type="date"
                required
                value={invoiceDate}
                disabled={invoiceLoading}
                onChange={(event) => {
                  setInvoiceDate(event.target.value);
                  setInvoiceError("");
                }}
                className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
              />
            </label>
            {invoiceError && (
              <p className="mt-2 text-sm font-medium text-red-600">{invoiceError}</p>
            )}
            <div className="mt-5 flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                disabled={invoiceLoading}
                onClick={() => setShowInvoiceDateModal(false)}
              >
                Cancel
              </Button>
              <Button
                type="button"
                disabled={invoiceLoading || !invoiceDate}
                onClick={handleSubmit}
                className="bg-purple-600 text-white hover:bg-purple-700"
              >
                {invoiceLoading ? "Generating..." : "Confirm"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </Card>
  );
});
